import React, { useEffect, useState } from "react";
import HeaderLayout from "../../../../components/HeaderLayout";
import InputTextField from "../../../../components/InputTextField";
import FormikSubmitButton from "../../../../components/FormikSubmitButton";
import { useTheme } from "@emotion/react";
import { useNavigate, useParams } from "react-router";
import PermissionAccordion from "../../../../components/PermissonAccordion";
import { Box, Button, CircularProgress, Snackbar, Alert } from "@mui/material";
import TextFieldLabel from "../../../../components/TextFieldLabel";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import api from "../../../../api/apiClient";

const validationSchema = Yup.object({
  role: Yup.string().required("Role is required"),
});

export default function AddEditRole() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(isEditMode);

  const [initialValues, setInitialValues] = useState({
    role: "",
    permissions: {
      Perkataan: { view: true, create: false, edit: false, delete: false },
      User: { view: false, create: false, edit: false, delete: false },
      Role: { view: false, create: false, edit: false, delete: false },
    },
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const navigateBack = () => {
    navigate(-1);
  };

  // Fetch existing role details when in edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const fetchRole = async () => {
      try {
        const res = await api.get(`/role/${id}`);
        if (res.data.success && res.data.data) {
          const { name, permissions } = res.data.data;

          const formattedPermissions = {
            Perkataan: permissions?.perkataan || {
              view: true,
              create: false,
              edit: false,
              delete: false,
            },
            Users: permissions?.user || {
              view: false,
              create: false,
              edit: false,
              delete: false,
            },
            Roles: permissions?.role || {
              view: false,
              create: false,
              edit: false,
              delete: false,
            },
          };

          setInitialValues({
            role: name || "",
            permissions: formattedPermissions,
          });
        }
      } catch (error) {
        console.error("Failed to fetch role:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRole();
  }, [id, isEditMode]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEditMode) {
        await api.put(`/role/${id}`, {
          name: values.role,
          permissions: values.permissions,
        });
        setSnackbar({
          open: true,
          message: "Role updated successfully!",
          severity: "success",
        });
      } else {
        await api.post("/role", {
          name: values.role,
          permissions: values.permissions,
        });
        setSnackbar({
          open: true,
          message: "Role added successfully!",
          severity: "success",
        });
      }

      // Delay navigation slightly to show snackbar first
      setTimeout(() => {
        navigate("/role-list");
      }, 800);
    } catch (error) {
      console.error("Submit error:", error.message);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="mr-1 ml-1">
      <div className="flex-shrink-0">
        <HeaderLayout name={isEditMode ? "Edit Role" : "Add Role"} />
      </div>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="overflow-y-auto h-[62vh] px-5 py-6 space-y-10 hide-scrollbar">
              <div className="px-[100px]">
                <InputTextField
                  label="Role Name"
                  requiredMark
                  name="role"
                  placeholder="Enter Role"
                  type="text"
                  className="w-full"
                />

                <div className="mt-10">
                  <TextFieldLabel label="Permission" />
                  <Box sx={{ margin: "auto", mt: 3 }}>
                    {Object.keys(values.permissions).map((module) => (
                      <PermissionAccordion
                        key={module}
                        moduleName={
                          module.charAt(0).toUpperCase() + module.slice(1)
                        }
                        permissions={values.permissions[module]}
                        onPermissionChange={(module, action, checked) => {
                          setFieldValue(
                            `permissions.${module}.${action}`,
                            checked
                          );
                        }}
                      />
                    ))}
                  </Box>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex-shrink-0 flex items-center gap-4 justify-center pt-4 border-t bg-white">
              <Button
                onClick={navigateBack}
                variant="contained"
                className="flex items-center justify-center border py-3 cursor-pointer text-white rounded w-[90px] h-[40px]"
                sx={{
                  backgroundColor: theme.palette.buttonHover.main,
                  "&:hover": {
                    backgroundColor: theme.palette.buttonHover.secondary,
                  },
                  color: theme.palette.white.main,
                  textTransform: "none",
                }}
              >
                Back
              </Button>
              <FormikSubmitButton
                name={isEditMode ? "Update" : "Submit"}
                className="flex items-center justify-center border py-3 cursor-pointer text-white rounded w-[90px] h-[40px]"
              />
            </div>
          </Form>
        )}
      </Formik>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
